// ============================================
// PRTCL (formerly PenRx) Type Definitions
// All TypeScript interfaces and enums
// ============================================

// ---- Enums ----

export enum PeptideCategory {
  GLP1 = 'GLP1',
  GH_SECRETAGOGUE = 'GH_Secretagogue',
  HEALING_RECOVERY = 'Healing_Recovery',
  ANTI_AGING = 'Anti_Aging',
  SEXUAL_HEALTH = 'Sexual_Health',
  COGNITIVE = 'Cognitive',
  IMMUNE = 'Immune',
  HRT = 'HRT',
  OTHER = 'Other',
}

export const CATEGORY_LABELS: Record<PeptideCategory, string> = {
  [PeptideCategory.GLP1]: 'GLP-1 & Weight Loss',
  [PeptideCategory.GH_SECRETAGOGUE]: 'Growth Hormone',
  [PeptideCategory.HEALING_RECOVERY]: 'Healing & Recovery',
  [PeptideCategory.ANTI_AGING]: 'Anti-Aging',
  [PeptideCategory.SEXUAL_HEALTH]: 'Sexual Health',
  [PeptideCategory.COGNITIVE]: 'Cognitive',
  [PeptideCategory.IMMUNE]: 'Immune',
  [PeptideCategory.HRT]: 'HRT',
  [PeptideCategory.OTHER]: 'Other',
};

export enum RegulatoryStatus {
  CATEGORY_1 = 'Category_1',
  CATEGORY_2 = 'Category_2',
  INVESTIGATIONAL = 'Investigational',
  RESEARCH = 'Research',
}

export const REGULATORY_LABELS: Record<RegulatoryStatus, string> = {
  [RegulatoryStatus.CATEGORY_1]: 'Category 1',
  [RegulatoryStatus.CATEGORY_2]: 'Category 2',
  [RegulatoryStatus.INVESTIGATIONAL]: 'Investigational',
  [RegulatoryStatus.RESEARCH]: 'Research Only',
};

export enum FrequencyPattern {
  DAILY = 'daily',
  SPECIFIC_DAYS = 'specific_days',
  EOD = 'eod',
  WEEKLY = 'weekly',
}

export const FREQUENCY_LABELS: Record<FrequencyPattern, string> = {
  [FrequencyPattern.DAILY]: 'Every Day',
  [FrequencyPattern.SPECIFIC_DAYS]: 'Specific Days',
  [FrequencyPattern.EOD]: 'Every Other Day',
  [FrequencyPattern.WEEKLY]: 'Once a Week',
};

export const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
export const DAY_LABELS_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;

export enum InjectionSite {
  ABDOMEN_LEFT = 'abdomen_left',
  ABDOMEN_RIGHT = 'abdomen_right',
  THIGH_LEFT = 'thigh_left',
  THIGH_RIGHT = 'thigh_right',
  ARM_LEFT = 'arm_left',
  ARM_RIGHT = 'arm_right',
  GLUTE_LEFT = 'glute_left',
  GLUTE_RIGHT = 'glute_right',
}

export const INJECTION_SITE_LABELS: Record<InjectionSite, string> = {
  [InjectionSite.ABDOMEN_LEFT]: 'Left Abdomen',
  [InjectionSite.ABDOMEN_RIGHT]: 'Right Abdomen',
  [InjectionSite.THIGH_LEFT]: 'Left Thigh',
  [InjectionSite.THIGH_RIGHT]: 'Right Thigh',
  [InjectionSite.ARM_LEFT]: 'Left Arm',
  [InjectionSite.ARM_RIGHT]: 'Right Arm',
  [InjectionSite.GLUTE_LEFT]: 'Left Glute',
  [InjectionSite.GLUTE_RIGHT]: 'Right Glute',
};

export enum SyringeType {
  U100 = 'U100',
  U50 = 'U50',
  U30 = 'U30',
}

export const SYRINGE_MAX_UNITS: Record<SyringeType, number> = {
  [SyringeType.U100]: 100,
  [SyringeType.U50]: 50,
  [SyringeType.U30]: 30,
};

// ---- Shared UI Constants (used by calculator, walkthrough, protocols) ----

export const DOSE_UNIT_OPTIONS = [
  { value: 'mcg', label: 'mcg' },
  { value: 'mg', label: 'mg' },
];

export const SYRINGE_OPTIONS = [
  { type: SyringeType.U30, label: '0.3 mL', units: 30, desc: '30 unit', img: '/images/syringe-03ml.png' },
  { type: SyringeType.U50, label: '0.5 mL', units: 50, desc: '50 unit', img: '/images/syringe-05ml.png' },
  { type: SyringeType.U100, label: '1.0 mL', units: 100, desc: '100 unit', img: '/images/syringe-10ml.png' },
];

export enum SideEffectSeverity {
  MILD = 'mild',
  MODERATE = 'moderate',
  RARE = 'rare',
}

export enum SideEffectFrequency {
  COMMON = 'common',
  OCCASIONAL = 'occasional',
  RARE = 'rare',
}

// ---- Peptide Library Types ----

export interface PeptideSideEffect {
  name: string;
  frequency: SideEffectFrequency;
  severity: SideEffectSeverity;
}

export interface ProtocolSource {
  name: string;
  url: string;
}

export interface PeptideProtocol {
  name?: string;              // e.g., "Standard Musculoskeletal Protocol"
  dosingRange: string;        // e.g., "200-500mcg"
  frequency: string;          // e.g., "1-2x daily"
  route: string;              // e.g., "Subcutaneous"
  duration: string;           // e.g., "4-6 weeks"
  cyclePattern?: string;      // e.g., "8 weeks on / 8-10 weeks off"
  notes?: string;
  source?: string;
  sources?: ProtocolSource[];
  // Distinguishes solo-peptide protocols from combined/stacked regimens so
  // the peptide detail page can route each into the right section. When
  // undefined (legacy rows), treated as 'solo'.
  type?: 'solo' | 'combined';
  combinedWith?: string[];    // peptide slugs this protocol pairs with (only set when type === 'combined')
  stackSlug?: string;         // optional deep-link to a stack detail page, e.g., 'cjc-ipamorelin-stack'
}

export interface Citation {
  title: string;
  url: string;
  type: 'pubmed' | 'clinicaltrials' | 'label' | 'review' | 'other';
}

export interface StackingPartner {
  peptideId: string;
  peptideName: string;
  notes: string;
  rationale?: string;
  typicalProtocol?: string;
}

export interface MechanismOfAction {
  plainEnglish: string;
  scientific: string;
}

export interface DoctorInsight {
  doctorName: string;
  credential: string;
  platform: string;
  insight: string;
  source: string;
  sourceUrl?: string;
}

export interface PublishedResearch {
  title: string;
  authors: string;
  year: number;
  journal: string;
  pmid: string;
  finding: string;
  url?: string;
}

export interface ReconstitutionInfo {
  typicalVialSize: string;
  recommendedBacWater: string;
  resultingConcentration: string;
  instructions: string;
  storageAfterReconstitution: string;
  alternativeVolumes: { bacWater: string; concentration: string }[];
}

export interface Pharmacokinetics {
  halfLife: { value: string; source: string };
  timeToMax: string;
  bioavailability: string;
  elimination: string;
}

export interface ExpandedSideEffects {
  common: PeptideSideEffect[];
  serious: PeptideSideEffect[];
  contraindications: string[];
  drugInteractions: string[];
}

// ---- Beginner Card (simplified detail view) ----

/** App Review 1.4.1: a recognized-source citation for a claim shown to the
 *  user. `name` is the human-readable reference (e.g. "Staresinic et al. 2003,
 *  J Orthop Res" or "Wegovy Prescribing Information (US)"). `url` is populated
 *  ONLY for recognized hosts (see isRecognizedSourceUrl in DetailPage) so the
 *  reference renders as a tap-through link; author-year references with no
 *  resolvable recognized URL render as plain text. We never fabricate a link. */
export interface SourceCitation {
  name: string;
  url?: string;
}

export interface BeginnerCard {
  oneLiner: string;
  category_label: string;
  usedFor: string[];
  /** App Review 1.4.1: recognized sources backing the usedFor claims (Overview). */
  usedFor_sources?: SourceCitation[];
  howItWorks_simple: string;
  /** App Review 1.4.1: recognized sources backing the howItWorks_simple text (Overview). */
  howItWorks_sources?: SourceCitation[];
  starterProtocol: {
    label: string;
    dose?: string;
    frequency?: string;
    route?: string;
    duration: string;
    cycle: string;
    note: string;
    components?: Array<{
      peptideName: string;
      dose: string;
      frequency: string;
      route: string;
    }>;
  } | null;
  storage_simple: {
    before_mixing: string;
    after_mixing: string;
  };
  reconstitution_simple: {
    vial_size: string;
    water: string;
    technique: string;
    result: string;
  };
  stackedWith: Array<{
    peptideId?: string;
    peptideName: string;
    why_simple: string;
    slug?: string;
  }>;
  peptides_in_stack?: Array<{
    peptideId?: string;
    peptideName: string;
    role_simple: string;
    slug?: string;
  }>;
  /** FREE (2026-07-23): attributed expert sources shown in Research. */
  trustedVoices: Array<{
    name: string;
    credential: string;
    title: string;
    content_type: string;
    platform: string;
    url?: string;
  }>;
  safetySnapshot: {
    common_side_effects: string;
    who_should_avoid: string;
    disclaimer: string;
    /** App Review 1.4.1: recognized sources backing the safety claims shown on
     *  the Safety tab. For research peptides with no published human safety data,
     *  the side-effects text is a cited evidence-gap note and these are its sources. */
    sources?: SourceCitation[];
  };
  /** FREE (2026-07-23): Research-section navigation and descriptions. */
  goDeeper: {
    sections: Array<{
      id: string;
      label: string;
      description: string;
    }>;
  };
}

// ---- knowledgeBase (v3.0 schema, Apr 2026) ----
//
// Tier-tagged, searchable knowledge chunks attributed to a specific source
// (video, paper, physician). Written by the Perplexity content pipeline
// into peptides.data.knowledgeBase as an array. The app reads these but
// does not write to them. Each chunk is self-contained so it can be
// surfaced in search, tooltips, or AI Q&A without re-fetching context.

export type KnowledgeBaseTier = 'beginner' | 'intermediate' | 'advanced';
export type KnowledgeBaseConfidence = 'high' | 'medium' | 'low';
export type KnowledgeBaseSourceTier =
  | 'tier_1_peer_reviewed'
  | 'tier_2_physician'
  | 'tier_3_practitioner'
  | 'tier_4_user';

export interface KnowledgeBaseSource {
  url: string;
  name: string;
  tier: KnowledgeBaseSourceTier;
  credential?: string;
  /** YouTube-specific metadata. Present when platform === 'YouTube'. */
  videoId?: string;
  videoTitle?: string;
  platform?: string;
  timestamp?: string;
}

export interface KnowledgeBaseChunk {
  /** Stable id, e.g. "bpc-157_tatem_001". Used as React key and for dedupe. */
  id: string;
  tier: KnowledgeBaseTier;
  /** Free-form category tag: "mechanism" | "protocol" | "safety" | "dosing"
   *  | "history" | etc. Not a strict enum - grows with the corpus. */
  category: string;
  content: string;
  confidence: KnowledgeBaseConfidence;
  source: KnowledgeBaseSource;
  tags: string[];
  /** peptide slugs this chunk references. Useful for cross-linking and
   *  recommending related reading. */
  relatedPeptides: string[];
}

export interface Peptide {
  id: string;
  name: string;
  alternateNames: string[];
  slug: string;
  category: PeptideCategory;
  shortDescription: string;
  fullDescription: string;
  /** PREMIUM (server-gated 2026-07-10): dosing protocols. Absent unless
   *  the user is a Plus subscriber - the DB strips it for free users. */
  commonProtocols?: PeptideProtocol[];
  reconstitutionGuide: string;
  storage: {
    temperature: string;
    lightSensitivity: boolean;
    shelfLife: string;
  };
  halfLife: {
    value: string;
    unit: string;
    source?: string;
  };
  sideEffects: PeptideSideEffect[];
  /** PREMIUM (server-gated 2026-07-10): legacy duplicate of rendered
   *  premium content; stripped from the free view. */
  stackingPartners?: StackingPartner[];
  regulatoryStatus: RegulatoryStatus;
  sources: Citation[];
  imageUrl?: string;
  tags: string[];
  // --- New fields from research database ---
  /** LEGACY + server-stripped (2026-07-10): dormant duplicates of the
   *  rendered premium content. Stripped from the free view and NOT
   *  re-exposed by the premium views, so at runtime these are undefined
   *  for EVERYONE (including Plus). Do not build features on them
   *  without first re-exposing them in peptides_premium. */
  mechanismOfAction?: MechanismOfAction;
  doctorInsights?: DoctorInsight[];
  publishedResearch?: PublishedResearch[];
  reconstitutionInfo?: ReconstitutionInfo;
  pharmacokinetics?: Pharmacokinetics;
  expandedSideEffects?: ExpandedSideEffects;
  regulatoryDetail?: string;
  molecularWeight?: string;
  aminoAcidSequence?: string;
  sequenceLength?: number;
  disclaimer?: string;
  lastUpdated?: string;
  beginnerCard?: BeginnerCard;
  /** FREE (2026-07-23). v3.0 schema: attributed knowledge chunks written
   *  by the Perplexity pipeline. Optional because older rows may not have
   *  been re-pushed yet. */
  knowledgeBase?: KnowledgeBaseChunk[];
}

// ---- Stack Types ----

export interface StackComponent {
  peptideId: string;
  peptideName: string;
  role: string;
  typicalDoseInStack: string;
  frequency: string;
  route: string;
  /**
   * v3 (T19): this peptide's mg in the stack's COMBINED-VIAL kit
   * recipe (e.g. GLOW 70 = GHK-Cu 50 + BPC-157 10 + TB-500 10).
   * Pre-fills the S3b recipe rows; always user-editable ("Your label
   * wins"). Only set on the named-combo stacks the wizard chips
   * offer; undefined elsewhere.
   */
  vialAmountMg?: number;
}

export interface StackProtocolInfo {
  name: string;
  description: string;
  duration: string;
  cyclePattern?: string;
  notes?: string;
  sources?: ProtocolSource[];
}

export interface StackDoctorInsight {
  doctorName: string;
  insight: string;
  source: string;
  sourceUrl?: string;
}

export interface StackResearch {
  title: string;
  year: number;
  source: string;
  url?: string;
  finding: string;
}

export interface PeptideStack {
  id: string;
  name: string;
  slug: string;
  category: PeptideCategory;
  popularityTier: number;
  shortDescription: string;
  fullDescription: string;
  components: StackComponent[];
  /** PREMIUM (server-gated 2026-07-10): absent for free users. */
  commonProtocols?: StackProtocolInfo[];
  calculatorNotes: string;
  primaryUses: string[];
  whoUsesThis: string;
  expectedTimeline: string;
  sideEffects: string[];
  contraindications: string[];
  regulatoryStatus: string;
  costRange: string;
  /** PREMIUM (server-gated 2026-07-10): legacy duplicates, stripped from
   *  the free view. */
  doctorInsights?: StackDoctorInsight[];
  publishedResearch?: StackResearch[];
  tags: string[];
  lastUpdated: string;
  disclaimer: string;
  beginnerCard?: BeginnerCard;

  // 2026-05-14: stacks whose components have incompatible dosing frequencies
  // (e.g. Wolverine, GLOW, CLO - TB-500 twice weekly vs BPC-157 daily) can't
  // physically exist as a single pre-mixed vial that satisfies all protocols
  // simultaneously. We mark those 'multi-vial-only' so the detail page shows
  // a banner explaining the constraint and replaces the primary Add Protocol
  // CTA with a softer affordance until the dedicated multi-peptide wizard ships.
  // Stacks without this field (or with 'single-vial' for future explicit-single)
  // render the original CTA unchanged.
  packagingMode?: 'multi-vial-only' | 'single-vial';
  packagingNotice?: {
    headline: string;
    body: string;
  };
}

// ---- Protocol Types ----

// ---- Protocol v2 Types ----

export interface ProtocolPhase {
  id: string;
  phaseNumber: number;
  label?: string;
  doseMcg: number;
  durationValue: number | null;
  durationUnit: 'days' | 'weeks' | 'months' | 'doses' | null;
  isMaintenance: boolean;
  notes?: string;

  // Optional schedule overrides - undefined = inherit from component
  frequencyOverride?: FrequencyPattern;
  timesOfDayOverride?: string[];
  selectedDaysOverride?: number[];
  mealTimingOverride?: 'fasted' | 'after_meal' | 'anytime' | null;

  // Conditional advance criteria
  conditionalAdvanceCriteria?: Array<{
    criterion: string;
    type: 'user_self_report' | 'duration_check' | 'biomarker_check';
  }>;

  // Source tracking
  source: 'user_manual' | 'video_extracted' | 'database_backfill';
  sourceMetadata?: {
    videoId?: string;
    databasePeptideId?: string;
    databaseProtocolId?: string;
    confidence?: 'high' | 'medium' | 'low';
    citation?: ProtocolSource;
  };
}

export interface ProtocolComponent {
  id: string;
  peptideId: string;
  peptideName: string;

  // Reconstitution / vial
  vialSizeMg: number;
  bacWaterMl: number;
  syringeType: SyringeType;
  concentrationMcgPerUnit: number;
  inventoryId?: string;

  // ---- Add Protocol v3 unified mixture model (spec D14) ----
  /**
   * Components sharing a mixGroupId form ONE combined vial (a mix).
   * Undefined = single-peptide vial. "Combined" is derived from group
   * size > 1, never stored as a flag. For combined components,
   * vialSizeMg is THIS peptide's mg in the shared vial and doseMcg on
   * each phase is the TRUE derived per-peptide amount - both written
   * only by deriveMixtureFields in calculations.ts.
   */
  mixGroupId?: string;
  /**
   * Combined components only: the user's draw on the syringe,
   * identical within a group, so the dashboard renders "draw to N
   * units" without re-deriving. Undefined for singles.
   */
  drawUnits?: number;

  // ---- Scheduled protocols (future start date, 2026-07-16) ----
  /**
   * True when this component's vial was PHYSICALLY UNMIXED at save time
   * (powder path, or a reused sealed stored combo) AND the protocol was
   * not starting immediately. The activation engine must not silently
   * flip such a vial - it prompts the user to mix on the start day and
   * only completes activation once the vial is no longer researching.
   * Undefined on all pre-feature protocols (treated as false).
   */
  requiresMixOnStart?: boolean;
  /**
   * User's answer to "want the guided mixing walkthrough when it's time?"
   * (wizard S5Powder). Only written alongside requiresMixOnStart. false =
   * experienced user, don't push the walkthrough at mix time. Undefined
   * (pre-feature protocols) = offer the guide, beginner-first.
   */
  mixGuide?: boolean;
  /**
   * True for C6 approximate vials (came as liquid, water unknown).
   * Activation must NOT stamp dose counts for these - the clamp that
   * hides supply counts for approximate vials survives the dormant
   * period. Undefined = normal vial.
   */
  approximate?: boolean;

  // Component-level default schedule (phases can override)
  frequency: FrequencyPattern;
  selectedDays?: number[];
  timesOfDay: string[];
  mealTiming?: 'fasted' | 'after_meal' | 'anytime' | null;

  // Phases - always >= 1
  phases: ProtocolPhase[];
  currentPhaseId?: string;

  // Hold extension state
  lastHoldExtendedAt?: string;
  holdExtensionDays?: number;

  // Delayed start - offset from protocol startDate
  startDelay?: { value: number; unit: 'days' | 'weeks' | 'months' };
}

export interface PhaseExplanation {
  componentId: string;
  phaseId: string;
  summary: string;
  whyChange: string;
  whatToWatchFor: string;
  userEdited?: boolean;
}

export interface ProtocolSourceAttribution {
  type: 'manual' | 'video_extracted' | 'database_template';
  videoId?: string;
  videoUrl?: string;
  creatorName?: string;
  databasePeptideId?: string;
  databaseProtocolName?: string;
  citationSource?: ProtocolSource;
}

export type ProtocolStatus = 'active' | 'paused' | 'completed' | 'scheduled';

export interface Protocol {
  id: string;
  name: string;
  status: ProtocolStatus;
  isActive: boolean;
  startDate: string;
  /**
   * Per-dose reminder preference, persisted (2026-07-15) so a Scheduled
   * protocol can create its reminders at activation rather than at save.
   * Optional for backwards-compat with rows written before the column
   * existed; treat undefined as true.
   */
  notifyEachDose?: boolean;
  notes?: string;
  components: ProtocolComponent[];

  // Cached AI-generated novice translation
  plainEnglishSummary?: string;
  userEditedSummary?: string;
  phaseExplanations?: PhaseExplanation[];
  summaryGeneratedAt?: string;
  summaryModel?: string;

  // Source attribution
  sourceAttribution?: ProtocolSourceAttribution;

  createdAt: string;
  updatedAt: string;
}

// ---- Dose Log Types ----

export interface DoseLog {
  id: string;
  /**
   * Nullable as of 2026-04-29: ad-hoc dose logs (logged against a vial
   * without a structured protocol) leave protocolId / componentId /
   * phaseId all null. Protocol-attached logs continue to populate all
   * three. UI code MUST handle null on these fields.
   */
  protocolId: string | null;
  componentId: string | null;
  phaseId: string | null;
  /**
   * Optional vial-of-record. Persists which vial the dose came from so
   * ad-hoc logs (with no protocol) can still be reconciled against
   * inventory. ON DELETE SET NULL on the FK — deleting a vial preserves
   * the dose history.
   */
  inventoryId?: string | null;
  peptideId: string;
  peptideName: string;
  doseMcg: number;
  units: number;
  injectionSite: InjectionSite;
  scheduledTime?: string;
  loggedTime: string;
  notes?: string;
  skipped: boolean;
  phaseReflection?: string;
  /**
   * v3 (2026-07-02): one physical injection of an N-peptide mix writes
   * N per-component rows sharing one administrationId. Stats, streaks,
   * and dose history count administrations, not rows. Read contract:
   * dedup key is `administrationId ?? id` (a legacy single log IS its
   * own administration) - use countAdministrations, never dedup inline.
   */
  administrationId?: string | null;
  createdAt: string;
}

// ---- Inventory Types ----

/**
 * One peptide's share of a combined vial's recipe. Stored on the
 * inventory row (mix_recipe jsonb) and used to re-derive per-peptide
 * amounts. amountMg is the mg of THIS peptide in the shared vial.
 */
export interface MixRecipeComponent {
  peptideId: string;
  peptideName: string;
  amountMg: number;
}

export interface InventoryItem {
  id: string;
  peptideId: string;
  peptideName: string;
  vialSizeMg: number;
  bacWaterMl: number;
  concentrationMcgPerUnit: number;
  dateReconstituted: string;  // ISO date
  expirationDate: string;     // ISO date
  /**
   * Nullable as of 2026-04-29: vials added without a planned dose-per-
   * injection have null totalDoses + dosesRemaining until either the
   * first dose log auto-backfills via the log_dose_with_inventory RPC,
   * or the user attaches the vial to a protocol that supplies the dose.
   * UI shows "log a dose to start counting" when these are null.
   */
  totalDoses: number | null;
  dosesUsed: number;
  dosesRemaining: number | null;
  isActive: boolean;
  /**
   * 2026-04-29: "Researching" mode. True for vials added but not yet
   * started (e.g., ordered ahead, not opened yet). Excluded from
   * running-low alerts and not surfaced as an ad-hoc dose-log target
   * until flipped to false. Distinct from isActive (which means the
   * vial exists in the user's library and isn't archived).
   */
  isResearching?: boolean;
  notes?: string;
  // ---- Add Protocol v3 mix identity (spec C3, additive) ----
  /**
   * Combined vials only: the protocol components' shared mixGroupId.
   * Null/undefined for single-peptide vials. For mixes, peptideId /
   * peptideName describe the lead component; mixDisplayName is the
   * user-facing vial name and mixRecipe holds the full per-peptide
   * breakdown. vialSizeMg is the TOTAL mg across the recipe.
   */
  mixGroupId?: string | null;
  /** e.g. "CJC-1295 + Ipamorelin combo". Null for singles. */
  mixDisplayName?: string | null;
  /** Per-peptide mg recipe. Null for singles. */
  mixRecipe?: MixRecipeComponent[] | null;
  createdAt: string;
}

/**
 * An EXISTING stored combo (mix) vial to reconstitute in the SAME atomic
 * write as a protocol insert (storage-mix wizard reuse, D4/D5). Produced by
 * buildProtocol only when a reused mix starts NOW; consumed by the
 * save_protocol_with_reconstituted_vial RPC, which flips is_researching=false
 * and stamps the counts in one write (the CHECK constraint
 * inventory_researching_has_no_dose_count forbids the in-between). Every
 * value is already derived (C7); the RPC only validates identity + ownership.
 */
export interface VialReconstitution {
  inventoryId: string;
  /** Real water: stored value for an already-mixed combo, or what the user
   *  entered at S7 for a sealed one. */
  bacWaterMl: number;
  concentrationMcgPerUnit: number;
  /** Real counts (never the powder null-clamp - we are starting it now). */
  totalDoses: number | null;
  dosesRemaining: number | null;
  /** Fresh 28-day clock, set ONLY when reconstituting a SEALED combo (it is
   *  being mixed for the first time now). Omitted for an already-mixed
   *  combo, whose clock started when it was actually mixed. When present,
   *  the save RPC overwrites the vial's date_reconstituted / expiration_date;
   *  when absent it leaves them. Mirrors the single-vial Start Using flow. */
  dateReconstituted?: string;
  expirationDate?: string;
  /** Identity guard: the stored vial's mix group + peptide set. */
  mixGroupId: string;
  peptideIds: string[];
}

// ---- Reminder Types ----

export interface Reminder {
  id: string;
  protocolId: string;
  /**
   * Single-component reminders: that component's id. v3 combined-group
   * reminders: the group's FIRST component id (the row needs a stable
   * component anchor for drift checks); mixGroupId identifies the group.
   */
  componentId: string;
  /** Group reminders carry the group display name here. */
  peptideName: string;
  isEnabled: boolean;
  lastNotified?: string;
  createdAt: string;

  // Phase snapshot
  phaseId: string;
  phaseLabel?: string;
  phaseNumber: number;
  totalPhases: number;

  // Dose snapshot
  doseMcg: number;
  doseUnitDisplay: string;
  // Syringe units snapshot - calculated at reminder creation from the
  // component's vial size + BAC water + syringe type. Used by the
  // dose-reminder notification body so users see the actual draw
  // amount ("20 units") instead of the abstract dose ("500mcg").
  // Optional for backward-compat with reminders created before this
  // field existed; falls back to dose-only formatting.
  units?: number;

  // Schedule snapshot
  frequency: FrequencyPattern;
  selectedDays?: number[];
  timesOfDay: string[];

  // Novice context snippet
  summarySnippet?: string;

  // Drift tracking
  lastDriftCheckedAt?: string;
  snapshotVersion: number;

  /**
   * v3 (2026-07-02): combined groups get ONE reminder carrying the
   * group's mixGroupId; notification actions route to the group RPC
   * (log) or N skip rows sharing one administrationId (skip).
   * Null/undefined for single-component reminders.
   */
  mixGroupId?: string | null;
}

// ---- User Profile Types ----

export interface UserProfile {
  id: string;
  displayName?: string;
  hasCompletedOnboarding: boolean;
  userType?: 'first_timer' | 'experienced' | 'exploring';
  hasAcceptedDisclaimer: boolean;
  notificationsEnabled: boolean;
  measurementUnit: 'imperial' | 'metric';
  darkMode: boolean;
  acceptedTermsAt?: string;
  acceptedDisclaimerAt?: string;
  disclaimerAcknowledgedAt?: string;
  subscriptionStatus: 'active' | 'trialing' | 'expired' | 'none';
  subscriptionStripeCustomerId?: string;
  subscriptionStripeSubscriptionId?: string;
  inviteCodeUsed?: string;
  // Onboarding progress flags (migration 20260423_add_user_profile_onboarding_flags.sql).
  // Set once and never cleared. hasCreatedFirstProtocol flips on first successful
  // protocol save; hasSeenTour flips when the guided dashboard tour completes or
  // is skipped.
  hasCreatedFirstProtocol: boolean;
  hasSeenTour: boolean;
  // Plan the user chose during pre-signup onboarding ('free' | 'monthly' |
  // 'annual'). Persisted from localStorage on first dashboard load to power a
  // later win-back flow (distinguishing a deliberate free-tier choice from an
  // abandoned checkout). Migration 20260715_add_profile_subscription_intent.sql.
  // Client-writable (not a protected column), unlike subscriptionStatus.
  subscriptionIntent?: string;
  createdAt: string;
  updatedAt: string;
}

// ---- Calculator Types ----

export interface CalculatorInputs {
  vialSizeMg: number | null;
  bacWaterMl: number | null;
  desiredDoseMcg: number | null;
  syringeType: SyringeType;
}

export interface CalculatorResults {
  concentrationMcgPerUnit: number;
  concentrationMcgPer01ml: number;
  unitsToFill: number;
  dosesPerVial: number;
  isValid: boolean;
  errors: string[];
}

// ---- Schedule Types ----

/**
 * One member of a combined group's ScheduledDose, carrying what the
 * log action needs to write that component's DoseLog row.
 */
export interface ScheduledDoseGroupComponent {
  componentId: string;
  phaseId: string;
  peptideId: string;
  peptideName: string;
  /** TRUE derived per-peptide mcg (written by deriveMixtureFields). */
  doseMcg: number;
}

export interface ScheduledDose {
  id: string;
  protocolId: string;
  /** For combined groups: the group's FIRST component id. */
  componentId: string;
  phaseId: string;
  peptideId: string;
  /** For combined groups: the group display name ("CJC-1295 + Ipamorelin"). */
  peptideName: string;
  /** The parent protocol's user-chosen name, when set. Titles the dose on
   *  the dashboard; peptideName drops to a subtitle. See doseDisplayTitle. */
  protocolName?: string | null;
  /** For combined groups: TOTAL mcg in the draw (sum of the breakdown). */
  doseMcg: number;
  /** For combined groups: the shared drawUnits. */
  units: number;
  scheduledTime: string;      // HH:mm format
  isCompleted: boolean;
  doseLogId?: string;
  mealTiming?: 'fasted' | 'after_meal' | 'anytime' | null;

  // ---- Add Protocol v3 group fields (spec E2) - absent for singles ----
  mixGroupId?: string;
  /** Per-component breakdown; length >= 2 exactly when mixGroupId set. */
  groupComponents?: ScheduledDoseGroupComponent[];
}

// ---- FAQ Types ----

export enum FaqCategory {
  BASICS_TECHNIQUE = 'basics_technique',
  SAFETY_SOURCING = 'safety_sourcing',
  WHAT_TO_EXPECT = 'what_to_expect',
  LEGAL_REGULATORY = 'legal_regulatory',
  PEPTIDE_DEEP_DIVES = 'peptide_deep_dives',
}

export const FAQ_CATEGORY_LABELS: Record<FaqCategory, string> = {
  [FaqCategory.BASICS_TECHNIQUE]: 'Basics & Technique',
  [FaqCategory.SAFETY_SOURCING]: 'Safety & Sourcing',
  [FaqCategory.WHAT_TO_EXPECT]: 'What to Expect',
  [FaqCategory.LEGAL_REGULATORY]: 'Legal & Regulatory',
  [FaqCategory.PEPTIDE_DEEP_DIVES]: 'Peptide Deep Dives',
};

export enum FaqSourceType {
  PEER_REVIEWED = 'peer_reviewed',
  PRESCRIBING_LABEL = 'prescribing_label',
  REGULATORY = 'regulatory',
  GOVERNMENT_HEALTH = 'government_health',
  MEDICAL_INSTITUTION = 'medical_institution',
  MEDICAL_ASSOCIATION = 'medical_association',
  NONPROFIT_MEDICAL = 'nonprofit_medical',
  ACADEMIC = 'academic',
  PHYSICIAN_INSTITUTE = 'physician_institute',
  PHYSICIAN_CLINIC = 'physician_clinic',
  TRIAL_READOUT_NEWS = 'trial_readout_news',
  ACADEMIC_PREPRINT = 'academic_preprint',
  BOOK = 'book',
}

export interface FaqSource {
  label: string;
  url: string;
  type: FaqSourceType;
}

export interface FaqIfThen {
  condition: string;
  action: string;
}

export interface FaqGoDeeper {
  key_facts: string[];
  sources: FaqSource[];
}

export interface FaqData {
  id: string;
  short_answer: string;
  if_then?: FaqIfThen[];
  go_deeper?: FaqGoDeeper;
  honesty_flag?: string;
  tags?: string[];
  related_feature?: {
    label: string;
    route: string;
  };
  related_faq_ids?: string[];
  last_reviewed?: string;
}

export interface Faq {
  id: string;
  question: string;
  category: FaqCategory;
  display_order: number;
  data: FaqData;
  created_at: string;
  updated_at: string;
}
